/* eslint-disable no-nested-ternary */

import { useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { ExclamationTriangle } from 'react-bootstrap-icons';
import { ErrorBoundary } from 'react-error-boundary';
import './styles/styles.css';
import { useLocation, useParams } from 'react-router-dom';
import { useGuestAuth } from '../../modules/hooks';
import { useTransaction } from '../../modules/hooks/useTransaction';
import { guestPortalLogger } from '../../modules/logger/loggers';
import { GuestPortalProvider } from '../Providers/GuestPortalV2';
import { useMonitoring } from '../Providers/Monitoring';
import { Feedback } from './Feedback';
import { GuestPortal } from './GuestPortal';
import { GuestRegistration } from './GuestRegistration';
import GuestPageHeader from './Header';

const GuestPortalCrashFallback = () => (
  <Container
    fluid
    className="login d-flex flex-column justify-content-center"
    id="guest-portal"
  >
    <Container className="content" fluid>
      <Row className="justify-content-md-center loginHeader">
        <ExclamationTriangle color="white" size={70} />
        <Col className="justify-content-md-center mt-16px" xs="auto" sm="auto">
          <h3 className="login-heading">Something went wrong</h3>
          <h5 className="error-subtitle">
            We encountered an unexpected error. Please try refreshing the page.
          </h5>
        </Col>
      </Row>
    </Container>
  </Container>
);

const GuestPageLayout = () => {
  const { pathname } = useLocation();
  const { reservationId } = useParams();

  const { token, generateAuthToken } = useGuestAuth()!;
  const { identifyUser } = useMonitoring();
  const { initSession, getTransactionContext } = useTransaction();

  useEffect(() => {
    document.title = 'Guest Portal';
    const sid = initSession();
    guestPortalLogger.info({ sessionId: sid }, 'Guest portal session started');
  }, []);

  useEffect(() => {
    if (!reservationId) return;

    if (token) return;

    generateAuthToken({ reservationId });
  }, [reservationId]);

  useEffect(() => {
    if (!token || !reservationId) return;

    identifyUser(reservationId, { reservationId });
  }, [token, reservationId, identifyUser]);

  const handleError = (error: Error, info: { componentStack: string }) => {
    guestPortalLogger.error(
      {
        ...getTransactionContext(),
        error: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        reservationId,
      },
      'Guest portal crashed',
    );
  };

  return (
    <GuestPortalProvider reservationId={reservationId || ''}>
      <ErrorBoundary fallback={<GuestPortalCrashFallback />} onError={handleError}>
        <Container
          id="guest-portal"
          fluid
          className="page-container"
          style={{ paddingLeft: '0px', paddingRight: '0px' }}
        >
          {token && (
            <Card
              className="card-item"
              style={{ paddingLeft: '0px', paddingRight: '0px' }}
            >
              {pathname.includes('/guest-registration/') ? (
                <>
                  <GuestPageHeader type="verify" />
                  <GuestRegistration reservationId={reservationId || ''} />
                </>
              ) : pathname.includes('/feedback/') ? (
                <>
                  <GuestPageHeader type="firstRegistration" />
                  <Feedback />
                </>
              ) : (
                <>
                  <GuestPageHeader type="firstRegistration" />
                  <GuestPortal />
                </>
              )}
            </Card>
          )}
        </Container>
      </ErrorBoundary>
    </GuestPortalProvider>
  );
};

export default GuestPageLayout;
